import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { App } from "./App";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  NotificationProvider,
  useSnackbar,
  AlertProvider,
  ToasterProvider,
} from "@components";
import { SWState } from "./libs/interfaces";

const ServiceWorkerWrapper = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Estado del Service Worker
  const [swState, setSwState] = React.useState<SWState>({
    isRegistered: false,
    isOnline: navigator.onLine,
    hasError: false,
    lastError: null,
    version: null,
    retryCount: 0,
  });

  const [storageReady, setStorageReady] = React.useState(false);
  const [storageError, setStorageError] = React.useState<string | null>(null);

  // ✅ CAMBIO CRÍTICO: Separar las referencias de inicialización
  const isInitializingStorageRef = React.useRef(false);
  const isInitializingSWRef = React.useRef(false);
  const messageListenerSetup = React.useRef(false);
  const activeNotificationsRef = React.useRef<Set<string>>(new Set());
  const swRegistrationRef = React.useRef<ServiceWorkerRegistration | null>(
    null
  );

  // Función helper para notificaciones únicas
  const enqueueUniqueSnackbar = React.useCallback(
    (message: React.ReactNode | string, options: any, uniqueId?: string) => {
      const notificationId =
        uniqueId ||
        (typeof message === "string" ? message : JSON.stringify(message));

      const isUpdateNotification =
        notificationId.includes("sw-update") ||
        (typeof message === "string" && message.includes("Nueva versión"));

      if (
        !isUpdateNotification &&
        activeNotificationsRef.current.has(notificationId)
      ) {
        console.log(`🚫 Notificación duplicada evitada: ${notificationId}`);
        return null;
      }

      if (isUpdateNotification) {
        const timestamp = Date.now();
        const updateId = `sw-update-${timestamp}`;
        activeNotificationsRef.current.add(updateId);
      } else {
        activeNotificationsRef.current.add(notificationId);
      }

      const modifiedOptions = {
        ...options,
        onClose: (event: any, reason: string, key: any) => {
          activeNotificationsRef.current.delete(notificationId);
          if (isUpdateNotification) {
            Array.from(activeNotificationsRef.current).forEach((id) => {
              if (typeof id === "string" && id.includes("sw-update")) {
                activeNotificationsRef.current.delete(id);
              }
            });
          }
          if (options.onClose) {
            options.onClose(event, reason, key);
          }
        },
      };

      return enqueueSnackbar(message, modifiedOptions);
    },
    [enqueueSnackbar]
  );

  const registerServiceWorker = React.useCallback(() => {
    if (
      isInitializingSWRef.current ||
      swState.isRegistered ||
      swRegistrationRef.current
    ) {
      return;
    }

    if (!("serviceWorker" in navigator)) {
      enqueueUniqueSnackbar(
        "Service Worker no soportado",
        { variant: "warning" },
        "sw-not-supported"
      );
      return;
    }

    isInitializingSWRef.current = true;

    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        swRegistrationRef.current = registration;
        isInitializingSWRef.current = false;

        setSwState((prev) => ({
          ...prev,
          isRegistered: true,
          hasError: false,
          lastError: null,
        }));

        // ✅ Setup inmediato
        setupRegistrationListeners(registration);
        setupMessageListener();

        if (registration.active) {
          getServiceWorkerVersion(registration.active);

          if (storageReady) {
            registration.active.postMessage({
              type: "STORAGE_READY",
              storageStats: { ready: storageReady, error: storageError },
            });
          }
        }

        enqueueUniqueSnackbar(
          "Aplicación lista para uso offline",
          { variant: "success", autoHideDuration: 3000 },
          "app-ready-offline"
        );

        // ✅ Verificación inicial de actualizaciones
        setTimeout(() => {
          registration.update().catch(console.warn);
        }, 3000);
      })
      .catch((error) => {
        isInitializingSWRef.current = false;
        swRegistrationRef.current = null;
        handleSWError(error, "Registro");
      });
  }, [swState.isRegistered, enqueueUniqueSnackbar, storageReady, storageError]);

  // ✅ SIMPLIFICADO: Manejo de errores del SW
  const handleSWError = React.useCallback(
    (error: any, context: string) => {
      console.error(`Service Worker Error (${context}):`, error);

      setSwState((prev) => ({
        ...prev,
        hasError: true,
        lastError: error.message || error.toString(),
        retryCount: prev.retryCount + 1,
      }));

      enqueueUniqueSnackbar(
        `Error en Service Worker: Funcionalidad offline limitada`,
        {
          variant: "error",
          persist: true,
          action: (key: any) => (
            <div className="sw_error_container">
              <button
                className="sw_action_btn sw_retry_btn"
                onClick={() => {
                  closeSnackbar(key);
                  // ✅ Reintentar solo el SW
                  isInitializingSWRef.current = false;
                  registerServiceWorker();
                }}
              >
                Reintentar
              </button>
              <button
                className="sw_action_btn sw_dismiss_btn"
                onClick={() => closeSnackbar(key)}
              >
                Cerrar
              </button>
            </div>
          ),
        },
        `sw-error-${context}-${Date.now()}`
      );
    },
    [enqueueUniqueSnackbar, closeSnackbar, registerServiceWorker]
  );

  // ✅ Obtener versión del SW (sin cambios)
  const getServiceWorkerVersion = React.useCallback((worker: ServiceWorker) => {
    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      if (event.data && event.data.version) {
        setSwState((prev) => ({ ...prev, version: event.data.version }));
        console.log("📦 Versión del SW:", event.data.version);
      }
    };

    worker.postMessage({ type: "GET_VERSION" }, [messageChannel.port2]);
  }, []);

  // ✅ SIMPLE: Setup de listeners - actualización automática obligatoria
  const setupRegistrationListeners = React.useCallback(
    (registration: ServiceWorkerRegistration) => {
      console.log("📡 Configurando listeners de actualización automática...");

      // ✅ Verificación automática cada 5 minutos
      const updateInterval = setInterval(() => {
        console.log("🔄 Verificación automática de actualizaciones...");
        registration.update().catch(console.warn);
      }, 5 * 60 * 1000);

      // ✅ SIMPLE: Cuando hay actualización, aplicar inmediatamente
      const handleUpdateFound = () => {
        const newWorker = registration.installing;
        console.log(
          "🎯 ACTUALIZACIÓN DETECTADA - Aplicando automáticamente..."
        );

        if (newWorker) {
          const handleStateChange = () => {
            console.log("🔄 Estado del nuevo worker:", newWorker.state);

            if (newWorker.state === "installed") {
              if (navigator.serviceWorker.controller) {
                // ✅ ACTUALIZACIÓN AUTOMÁTICA SIN OPCIONES
                console.log("✅ Aplicando actualización automáticamente...");

                // Mostrar notificación de que se está actualizando
                enqueueUniqueSnackbar(
                  "🔄 Nueva versión detectada - Actualizando automáticamente...",
                  {
                    variant: "info",
                    autoHideDuration: 3000,
                  },
                  `auto-updating-${Date.now()}`
                );

                // Aplicar actualización inmediatamente
                setTimeout(() => {
                  newWorker.postMessage({ type: "SKIP_WAITING" });

                  // Recargar página después de un momento
                  setTimeout(() => {
                    console.log("🔄 Recargando página - nueva versión activa");
                    window.location.reload();
                  }, 2000);
                }, 1000);
              } else {
                // Primera instalación
                console.log("📦 Primera instalación del Service Worker");
                enqueueUniqueSnackbar(
                  "✅ Aplicación lista para uso offline",
                  { variant: "success", autoHideDuration: 3000 },
                  "first-install"
                );
              }
            }

            if (newWorker.state === "activated") {
              console.log("✅ Nueva versión activada correctamente");
              enqueueUniqueSnackbar(
                "✅ Aplicación actualizada a la nueva versión",
                { variant: "success", autoHideDuration: 3000 },
                "update-success"
              );
            }
          };

          newWorker.addEventListener("statechange", handleStateChange);
          handleStateChange();
        }
      };

      registration.addEventListener("updatefound", handleUpdateFound);

      // ✅ Si ya hay un SW esperando, activarlo inmediatamente
      if (registration.waiting) {
        console.log("⚡ SW esperando detectado - Activando inmediatamente...");

        enqueueUniqueSnackbar(
          "🔄 Aplicando actualización pendiente...",
          { variant: "info", autoHideDuration: 5000 },
          "applying-pending"
        );

        setTimeout(() => {
          registration.waiting?.postMessage({ type: "SKIP_WAITING" });
          setTimeout(() => window.location.reload(), 1000);
        }, 1000);
      }

      // ✅ Cleanup simple
      return () => {
        clearInterval(updateInterval);
        registration.removeEventListener("updatefound", handleUpdateFound);
      };
    },
    [enqueueUniqueSnackbar]
  );

  // ✅ Message listener simplificado
  const setupMessageListener = React.useCallback(() => {
    if (messageListenerSetup.current) {
      console.log("🚫 Message listener ya configurado");
      return;
    }

    messageListenerSetup.current = true;
    console.log("📡 Configurando message listener");

    const messageHandler = (event: MessageEvent) => {
      const data = event.data as any;
      console.log("📬 Mensaje del SW:", data);

      // ✅ Mantener tu switch existente pero simplificado
      switch (data.type) {
        case "SYNC_COMPLETED":
        case "SYNC_COMPLETED_PERSISTENT":
          enqueueUniqueSnackbar(
            data.message || "Sincronización completada",
            { variant: "success", autoHideDuration: 4000 },
            `sync-${Date.now()}`
          );
          break;

        case "SIMPLE_QUEUE_PROCESSED_PERSISTENT":
          if (data.totalSuccessCount > 0) {
            let message = `${data.totalSuccessCount} elementos sincronizados`;
            if (data.duplicateSuccessCount > 0) {
              message += ` (${data.duplicateSuccessCount} ya existían)`;
            }

            enqueueUniqueSnackbar(
              message,
              { variant: "success", autoHideDuration: 4000 },
              `simple-queue-${Date.now()}`
            );
          }
          break;

        case "NEW_VERSION_AVAILABLE":
          enqueueUniqueSnackbar(
            `Nueva versión disponible: ${data.version}`,
            { variant: "info" },
            `new-version-${data.version}`
          );
          break;

        case "MANUAL_SYNC_ERROR":
          enqueueUniqueSnackbar(
            `Error al sincronizar manualmente`,
            {
              variant: "error",
            },
            `${data.error}`
          );
          break;

        case "COMPLETE_CLEANUP_RESULT":
          enqueueUniqueSnackbar(
            `🧹 Limpieza completa: ${data.totalCleaned} elementos eliminados`,
            {
              variant: "success",
              autoHideDuration: 5000,
              action: (key: any) => (
                <button
                  onClick={() => {
                    closeSnackbar(key);
                    window.location.reload();
                  }}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    marginLeft: "8px",
                  }}
                >
                  Recargar
                </button>
              ),
            },
            `cleanup-completed-${Date.now()}`
          );
          break;

        case "COMPLETE_CLEANUP_ERROR":
          enqueueUniqueSnackbar(
            "❌ Error en limpieza completa",
            { variant: "error", autoHideDuration: 4000 },
            `cleanup-error-${Date.now()}`
          );
          break;

        case "RELOAD_PAGE":
          window.location.reload();
          break;

        // ✅ Agregar otros cases importantes pero mantener simple
        default:
          console.log("📬 Mensaje del SW:", data.type);
      }
    };

    navigator.serviceWorker.addEventListener("message", messageHandler);

    return () => {
      navigator.serviceWorker.removeEventListener("message", messageHandler);
      messageListenerSetup.current = false;
    };
  }, [enqueueUniqueSnackbar]);

  React.useEffect(() => {
    let mounted = true;

    const initApp = async () => {
      if (!mounted) return;

      // 1. Registrar el Service Worker primero
      const registerSWAndWait = async () => {
        return new Promise<void>((resolve) => {
          if (document.readyState === "complete") {
            registerServiceWorker();
          } else {
            const handleLoad = () => {
              if (mounted) registerServiceWorker();
              window.removeEventListener("load", handleLoad);
            };
            window.addEventListener("load", handleLoad);
          }

          // Esperar a que el SW esté listo
          navigator.serviceWorker.ready.then(() => {
            resolve();
          });
        });
      };

      await registerSWAndWait();

      // 2. Ahora sí, inicializa unifiedStorage
      // await initializeUnifiedStorage();

      setTimeout(() => {
        if (window.hideInitialLoading) {
          window.hideInitialLoading();
        }
      }, 2000);
    };

    initApp();

    window.dispatchEvent(new Event("ReactAppLoaded"));
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    // Solicitar persistent storage
    if (navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().then((granted) => {
        if (granted) {
          console.log("✅ Storage persistente activado");
        } else {
          console.log("⚠️ Storage persistente no otorgado");
        }
      });
    }
  }, []);

  // ✅ Conectividad (sin cambios)
  React.useEffect(() => {
    const handleOnline = () => {
      setSwState((prev) => ({ ...prev, isOnline: true }));
      enqueueUniqueSnackbar(
        "Conexión restaurada",
        { variant: "success" },
        "online"
      );
    };

    const handleOffline = () => {
      setSwState((prev) => ({ ...prev, isOnline: false }));
      enqueueUniqueSnackbar(
        "Sin conexión - Modo offline",
        { variant: "info" },
        "offline"
      );
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [enqueueUniqueSnackbar]);

  // ✅ Cleanup
  React.useEffect(() => {
    return () => {
      isInitializingStorageRef.current = false;
      isInitializingSWRef.current = false;
      messageListenerSetup.current = false;
      swRegistrationRef.current = null;
      activeNotificationsRef.current.clear();
    };
  }, []);

  return null;
};

// ✅ React Query y App (sin cambios)
const client = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (!navigator.onLine && error?.name === "TypeError") {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <QueryClientProvider client={client}>
    <ToasterProvider defaultPlacement="topRight">
      <NotificationProvider>
        <AlertProvider>
          <ServiceWorkerWrapper />
          <App />
        </AlertProvider>
      </NotificationProvider>
    </ToasterProvider>
  </QueryClientProvider>
);
